// LEVEL-UP BURST (owner 2026-11-02: "need some better effects for leveling up"). Original procedural effect on the hero:
// a radial ki flare, a light column rising out of the ground, an expanding ground ring with a thin shockwave ahead of it,
// upward-streaming motes in the hero's current energy colours (the Surge level's palette when transformed), and two
// envelopes main.ts / view.ts read: `bloom` (a short bloom pulse) and `push` (nearby foes are DRAWN nudged outward and
// back — the sim positions never change). Evolution / fusion picks get a stronger gold / emerald confirm burst.
//
// Runs on REAL time (view.sync's presentDt): the level-up freezes the world (dt = 0) while this plays on the frozen frame.
// Cheap on phones: 4 draws (column, ground quad = ring + shockwave + glow disc, flare, one instanced mote batch), no lights
// (a new light would recompile every lit shader), mote count scales with the particle density setting. Everything is
// built up front and warmed in prewarm.ts. Flash rules: the flare + bloom pulse are one "big flash" (FLASH.big()); the
// rest follows FLASH.dim / FLASH.L.white (src/render/a11y.ts).
import * as THREE from 'three/webgpu';
import { abs, atan, dot, float, max, mix, normalView, positionViewDirection, pow, sin, smoothstep, uniform, uv, vec2 } from 'three/tsl';
import { FLASH } from './a11y';
import { softDotTexture } from './fx';

export type BurstKind = 'level' | 'evolve' | 'fusion';
/** confirm palettes (core, edge) — evolution gold, fusion emerald (the card colours) */
const CONFIRM: Record<Exclude<BurstKind, 'level'>, [number, number]> = { evolve: [0xfff3c4, 0xffb81e], fusion: [0xdcffee, 0x2ee89a] };

/** timeline (s) */
const LIFE = 1.25, GATHER_MAX = 0.8;
const ease = (x: number) => 1 - (1 - Math.min(1, Math.max(0, x))) ** 3;
const clamp01 = (x: number) => Math.min(1, Math.max(0, x));

/* eslint-disable @typescript-eslint/no-explicit-any */
function additive(): THREE.MeshBasicNodeMaterial {
  return new THREE.MeshBasicNodeMaterial({ transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide, fog: false });
}

export class LevelUpFx {
  readonly group = new THREE.Group();
  /** bloom pulse 0..~1.2 (main multiplies bloom strength by 1 + bloom) */
  bloom = 0;
  /** foe push envelope 0..1 around (cx, cz) within pushR — view.ts offsets drawn foes by it */
  push = 0; pushR = 7.5; cx = 0; cz = 0;
  /** QA: bursts played, last kind */
  stats = { bursts: 0, gathers: 0, kind: '' as string, count: 0 };

  private uCore = uniform(new THREE.Color(0xdff8ff)); private uEdge = uniform(new THREE.Color(0x4ab8ff));
  private uT = uniform(0);
  private uPillar = uniform(0);
  private uR1 = uniform(0); private uK1 = uniform(0); private uR2 = uniform(0); private uK2 = uniform(0); private uKd = uniform(0);
  private uFlare = uniform(0); private uRot = uniform(0);
  private pillar: THREE.Mesh; private ground: THREE.Mesh; private flare: THREE.Mesh; private motes: THREE.InstancedMesh;
  private readonly cap: number;
  /** mote state: x y z vx vy vz life max size kind(0 rise, 1 converge) */
  private d: Float32Array; private n = 0; private static S = 10;
  private age = -1; private gatherT = -1; private gatherDur = 0;
  private scale = 1; private flashK = 1; private kind: BurstKind = 'level';
  private ox = 0; private oy = 0; private oz = 0;
  private core = new THREE.Color(); private edge = new THREE.Color();
  private _m = new THREE.Matrix4(); private _p = new THREE.Vector3(); private _s = new THREE.Vector3(); private _c = new THREE.Color();

  /** @param density particle pool size of the quality preset / density setting (900 low … 3500 high) */
  constructor(density: number) {
    this.cap = Math.round(48 * Math.min(1.35, Math.max(0.5, density / 2200)));
    this.group.name = 'levelup-fx';
    // --- light column: open cylinder, base at y = 0 (uv.y 0 → 1 up the column); brightest where it faces the camera
    const pm = additive();
    const y = uv().y;
    const facing = abs(dot(normalView, positionViewDirection));
    const coreK = pow(facing, float(2.2));
    const vfade = smoothstep(0, 0.05, y).mul(pow(float(1).sub(y), float(1.3)));
    const bands = pow(sin(y.mul(30).sub(this.uT.mul(18))).mul(0.5).add(0.5), float(3)).mul(0.45);
    pm.colorNode = mix(this.uEdge, this.uCore, coreK).mul(coreK.mul(0.85).add(0.2)).mul(vfade).mul(bands.add(1)).mul(this.uPillar);
    this.pillar = new THREE.Mesh(new THREE.CylinderGeometry(1, 1, 1, 24, 1, true).translate(0, 0.5, 0), pm);
    // --- ground quad: soft expanding ring (+ faint fill), thin shockwave ahead of it, glow disc under the hero (unit = 1)
    const gm = additive();
    const q = uv().sub(vec2(0.5, 0.5)).mul(2), dd = q.length(), an = atan(q.y, q.x);
    const spikes = sin(an.mul(14).add(this.uT.mul(2))).mul(0.18).add(0.82);
    const band = float(1).sub(smoothstep(0, 0.11, abs(dd.sub(this.uR1)))); // the soft ring
    const line = float(1).sub(smoothstep(0, 0.018, abs(dd.sub(this.uR1.mul(0.985))))); // its hot leading line
    const ring1 = band.mul(spikes).add(float(1).sub(smoothstep(0, max(this.uR1, 0.001), dd)).mul(0.16)).mul(this.uK1);
    const ring2 = float(1).sub(smoothstep(0, 0.028, abs(dd.sub(this.uR2)))).mul(this.uK2);
    const disc = pow(float(1).sub(smoothstep(0, 0.3, dd)), float(2)).mul(this.uKd);
    gm.colorNode = this.uEdge.mul(ring1.mul(1.5)).add(this.uCore.mul(line.mul(this.uK1).mul(0.9))).add(mix(this.uEdge, this.uCore, 0.35).mul(ring2.mul(1.3)))
      .add(mix(this.uEdge, this.uCore, 0.4).mul(disc)).mul(float(1).sub(smoothstep(0.96, 1, dd)));
    this.ground = new THREE.Mesh(new THREE.PlaneGeometry(2, 2).rotateX(-Math.PI / 2), gm);
    // --- radial ki flare (camera-facing): 12 long rays + 18 fine ones around a hot core
    const fm = additive();
    const f = uv().sub(vec2(0.5, 0.5)).mul(2), fr = f.length(), fa = atan(f.y, f.x), fall = max(float(1).sub(fr), 0);
    const rays = pow(abs(sin(fa.mul(6).add(this.uRot))), float(16)).mul(pow(fall, float(1.1)));
    const fine = pow(abs(sin(fa.mul(9).sub(this.uRot.mul(1.4)))), float(28)).mul(pow(fall, float(2))).mul(0.55);
    const glow = pow(fall, float(4));
    fm.colorNode = mix(this.uEdge, this.uCore, glow.mul(1.6).min(1)).mul(rays.add(fine).add(glow.mul(1.5))).mul(this.uFlare);
    this.flare = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), fm);
    // --- motes: one instanced batch of soft dots (instance colour = brightness)
    const mm = additive(); mm.map = softDotTexture();
    this.motes = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1), mm, this.cap);
    this.motes.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(this.cap * 3), 3);
    this.motes.frustumCulled = false; this.motes.count = 0;
    this.d = new Float32Array(this.cap * LevelUpFx.S);
    for (const o of [this.pillar, this.ground, this.flare, this.motes]) { o.renderOrder = 6; o.visible = false; this.group.add(o); }
  }

  /** true while anything is still playing (main keeps the menu frame rate up meanwhile) */
  get active(): boolean { return this.age >= 0 || this.gatherT >= 0 || this.n > 0; }

  private colours(kind: BurstKind, spark: THREE.Color, aura: THREE.Color): void {
    if (kind === 'level') { this.core.copy(spark).lerp(_white, 0.45); this.edge.copy(aura); }
    else { this.core.setHex(CONFIRM[kind][0]); this.edge.setHex(CONFIRM[kind][1]); }
    // Reduce flashing: the near-white core is what reads as a strobe
    if (FLASH.mode !== 'off') this.core.lerp(this.edge, 1 - FLASH.L.white);
    (this.uCore.value as THREE.Color).copy(this.core); (this.uEdge.value as THREE.Color).copy(this.edge);
  }

  /** hero charge-up (the ?lupose action): motes drawn in to the chest for `dur` s before the burst */
  gather(x: number, y: number, z: number, dur: number, spark: THREE.Color, aura: THREE.Color): void {
    this.colours('level', spark, aura);
    this.ox = x; this.oy = y; this.oz = z;
    this.gatherT = 0; this.gatherDur = Math.min(GATHER_MAX, Math.max(0.1, dur));
    const k = Math.round(this.cap * 0.45);
    for (let i = 0; i < k; i++) {
      const a = Math.random() * Math.PI * 2, r = 2.2 + Math.random() * 1.4, h = 0.2 + Math.random() * 2;
      const T = this.gatherDur * (0.55 + Math.random() * 0.45); // arrive at the chest within the action
      const tx = x, ty = y + 1.15, tz = z, px = x + Math.cos(a) * r, pz = z + Math.sin(a) * r;
      this.spawn(px, y + h, pz, (tx - px) / T, (ty - y - h) / T, (tz - pz) / T, T, 0.1 + Math.random() * 0.1, 1);
    }
    this.flare.visible = true; this.stats.gathers++;
  }

  /** the burst itself: kind 'level' uses the hero's live energy colours; 'evolve' / 'fusion' the confirm palettes */
  burst(x: number, y: number, z: number, kind: BurstKind, count: number, spark: THREE.Color, aura: THREE.Color): void {
    this.colours(kind, spark, aura);
    this.kind = kind; this.ox = x; this.oy = y; this.oz = z; this.cx = x; this.cz = z;
    this.scale = (kind === 'level' ? 1 : 1.3) * (1 + Math.min(3, Math.max(0, count - 1)) * 0.08); // ×N reads a little bigger
    this.age = 0; this.gatherT = -1;
    this.flashK = FLASH.big(); // flare + bloom pulse = one big flash in the Reduce flashing budget
    const S = this.scale, k = Math.min(this.cap, Math.round(this.cap * (kind === 'level' ? 0.75 : 1)));
    for (let i = 0; i < k; i++) {
      const a = Math.random() * Math.PI * 2, r = 0.25 + Math.random() * 1.5 * S, up = 5 + Math.random() * 9 * S, sw = 1.6 + Math.random() * 1.6;
      this.spawn(x + Math.cos(a) * r, y + 0.1 + Math.random() * 0.6, z + Math.sin(a) * r, -Math.sin(a) * sw + Math.cos(a) * 1.2, up, Math.cos(a) * sw + Math.sin(a) * 1.2,
        0.6 + Math.random() * 0.6, 0.14 + Math.random() * 0.2, 0);
    }
    for (const o of [this.pillar, this.ground, this.flare]) o.visible = true;
    this.stats.bursts++; this.stats.kind = kind; this.stats.count = count;
  }

  /** drop everything (a new run / title) */
  clear(): void {
    this.age = -1; this.gatherT = -1; this.n = 0; this.bloom = 0; this.push = 0;
    for (const o of [this.pillar, this.ground, this.flare, this.motes]) o.visible = false;
  }

  private spawn(x: number, y: number, z: number, vx: number, vy: number, vz: number, life: number, size: number, kind: number): void {
    if (this.n >= this.cap) return;
    const i = this.n++ * LevelUpFx.S, d = this.d;
    d[i] = x; d[i + 1] = y; d[i + 2] = z; d[i + 3] = vx; d[i + 4] = vy; d[i + 5] = vz; d[i + 6] = life; d[i + 7] = life; d[i + 8] = size; d[i + 9] = kind;
  }

  /** advance on real time. hx/hy/hz = where the hero is drawn (the column and flare stay on him) */
  update(dt: number, camQ: THREE.Quaternion, hx: number, hy: number, hz: number): void {
    if (!this.active) return;
    this.uT.value = (this.uT.value as number) + dt;
    const dim = FLASH.dim, big = this.flashK;
    // --- gather: a growing glow at the chest
    let flareK = 0, flareS = 1;
    if (this.gatherT >= 0) {
      this.gatherT += dt; const g = clamp01(this.gatherT / this.gatherDur);
      flareK = 0.35 * g * g * dim; flareS = 0.6 + 1.1 * g;
      this.flare.position.set(hx, hy + 1.15, hz);
      if (this.gatherT > this.gatherDur + 0.3) this.gatherT = -1; // (the burst normally takes over before this)
    }
    // --- burst
    if (this.age >= 0) {
      this.age += dt; const a = this.age, S = this.scale;
      // flare: snaps on, decays by ~0.24 s
      const fk = a < 0.035 ? a / 0.035 : Math.max(0, 1 - (a - 0.035) / 0.21) ** 2;
      flareK = Math.max(flareK, 1.35 * fk * big); flareS = (2.4 + 2.6 * ease(a / 0.24)) * S;
      this.flare.position.set(hx, hy + 1.15, hz);
      this.uRot.value = a * 1.6;
      // column: shoots up in ~0.2 s, holds, thins into a beam and fades by ~1 s
      const rise = ease(a / 0.2), fade = 1 - clamp01((a - 0.38) / 0.62);
      this.pillar.position.set(hx, hy - 0.05, hz);
      const w = (0.8 + 0.7 * (1 - rise)) * S * (0.35 + 0.65 * fade);
      this.pillar.scale.set(w, Math.max(0.01, 11 * S * rise), w);
      this.uPillar.value = (1.6 + 0.9 * (1 - clamp01(a / 0.15))) * fade * dim * (this.kind === 'level' ? 1 : 1.15);
      // ground: ring + shockwave (radius in units of R), glow disc
      const R = 9.5 * S;
      this.ground.position.set(this.ox, this.oy + 0.07, this.oz); this.ground.scale.setScalar(R);
      this.uR1.value = 0.8 * ease(a / 0.55); this.uK1.value = 1.1 * Math.max(0, 1 - a / 0.75) * dim;
      this.uR2.value = 0.97 * ease(a / 0.34); this.uK2.value = 1.5 * Math.max(0, 1 - a / 0.4) * dim;
      this.uKd.value = 1.2 * (a < 0.05 ? a / 0.05 : Math.max(0, 1 - (a - 0.05) / 0.6)) * dim;
      // envelopes
      this.bloom = 1.1 * (a < 0.05 ? a / 0.05 : Math.max(0, 1 - (a - 0.05) / 0.32)) * big;
      this.push = a < 0.13 ? ease(a / 0.13) : Math.max(0, 1 - (a - 0.13) / 0.45) ** 2;
      if (a > LIFE) { this.age = -1; this.bloom = 0; this.push = 0; this.pillar.visible = false; this.ground.visible = false; }
    }
    this.flare.visible = flareK > 0.001;
    if (this.flare.visible) { this.flare.quaternion.copy(camQ); this.flare.scale.setScalar(flareS); this.uFlare.value = flareK; }
    // --- motes
    const d = this.d, S = LevelUpFx.S;
    for (let k = 0; k < this.n; k++) {
      const i = k * S;
      d[i + 6] -= dt;
      if (d[i + 6] <= 0) { this.n--; if (k !== this.n) d.copyWithin(i, this.n * S, this.n * S + S); k--; continue; }
      if (d[i + 9] === 0) { const drag = Math.exp(-1.6 * dt); d[i + 3] *= drag; d[i + 5] *= drag; d[i + 4] *= Math.exp(-0.9 * dt); }
      d[i] += d[i + 3] * dt; d[i + 1] += d[i + 4] * dt; d[i + 2] += d[i + 5] * dt;
    }
    const m = this.motes, inst = m.instanceColor!.array as Float32Array;
    for (let k = 0; k < this.n; k++) {
      const i = k * S, u = d[i + 6] / d[i + 7], conv = d[i + 9] === 1;
      const life = conv ? Math.min(1, (1 - u) * 3) : Math.min(1, u * 1.6);
      const sz = d[i + 8] * (conv ? 0.7 + 0.6 * (1 - u) : 0.5 + 0.5 * u);
      this._p.set(d[i], d[i + 1], d[i + 2]); this._s.set(sz, sz * (conv ? 1 : 1.9), sz); // rising motes stretch upward
      m.setMatrixAt(k, this._m.compose(this._p, camQ, this._s));
      this._c.copy(k % 3 ? this.edge : this.core).multiplyScalar(3 * life * dim);
      inst[k * 3] = this._c.r; inst[k * 3 + 1] = this._c.g; inst[k * 3 + 2] = this._c.b;
    }
    m.count = this.n; m.visible = this.n > 0;
    if (this.n) { m.instanceMatrix.needsUpdate = true; m.instanceColor!.needsUpdate = true; }
  }

  /** drawn-only outward nudge (m) for a foe at (x, z); 0 when no burst is pushing */
  pushAt(x: number, z: number, out: { x: number; z: number }): boolean {
    if (this.push <= 0.001) return false;
    const dx = x - this.cx, dz = z - this.cz, dist = Math.hypot(dx, dz);
    if (dist >= this.pushR || dist < 1e-3) return false;
    const f = this.push * 0.85 * this.scale * (1 - dist / this.pushR) / dist;
    out.x = dx * f; out.z = dz * f; return true;
  }
}
const _white = new THREE.Color(1, 1, 1);
